import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, KeyRound, AlertCircle } from 'lucide-react'
import { useRedeemKey } from '../hooks/useActivationKey'
import type { PaperbackSubject } from '../types'
import { formatActivationKey, isCompleteActivationKey, getRedeemErrorMessage } from '../utils/activationKey'

const SUBJECT_LABELS: Record<PaperbackSubject, string> = {
  PHYSICS: 'Physics',
  CHEMISTRY: 'Chemistry',
  BIOLOGY: 'Biology',
}

interface Props {
  userId: string
  testSubjects: PaperbackSubject[]
  testName?: string
  onSuccess: () => void
}

export default function ActivationKeyGate({ userId, testSubjects, testName, onSuccess }: Props) {
  const navigate = useNavigate()
  const [inputValue, setInputValue] = useState('')
  const [apiError, setApiError] = useState<string | null>(null)

  const mutation = useRedeemKey(userId)

  function handleInput(e: React.ChangeEvent<HTMLInputElement>) {
    setApiError(null)
    setInputValue(formatActivationKey(e.target.value))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const code = inputValue.replace(/-/g, '')
    if (code.length < 8) return

    try {
      const result = await mutation.mutateAsync(inputValue)
      if (result.data?.success) {
        onSuccess()
      } else {
        setApiError(result.data?.message ?? result.message ?? 'Redemption failed.')
      }
    } catch (err: unknown) {
      setApiError(getRedeemErrorMessage(err))
    }
  }

  const subjectLabel = testSubjects.map((s) => SUBJECT_LABELS[s]).join(' / ')
  const isValidLength = isCompleteActivationKey(inputValue)
  const isPending = mutation.isPending

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Fixed Header */}
      <header className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 z-10">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium text-sm">Dashboard</span>
          </button>
          <div className="text-center">
            <h1 className="text-base font-semibold text-gray-900 leading-tight">
              {testName ?? 'Solutions'}
            </h1>
          </div>
          <div className="w-20" />
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 overflow-y-auto flex items-center justify-center p-6">
        <div className="w-full max-w-sm space-y-6">
          {/* Icon + heading */}
          <div className="text-center space-y-2">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center">
                <KeyRound className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-gray-900">Activate Your Solutions</h2>
            <p className="text-sm text-gray-500">
              Enter the activation key found inside your paperback test book.
            </p>
            {subjectLabel && (
              <p className="text-sm font-medium text-blue-700 bg-blue-50 rounded-lg py-1.5 px-3 inline-block">
                Subject: {subjectLabel}
              </p>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <input
                type="text"
                value={inputValue}
                onChange={handleInput}
                placeholder="XXXX-XXXX"
                maxLength={9}
                autoCapitalize="characters"
                spellCheck={false}
                className="w-full text-center text-xl font-mono tracking-widest border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-300"
              />
            </div>

            {apiError && (
              <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{apiError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={!isValidLength || isPending}
              className="w-full bg-blue-600 text-white font-semibold py-3 rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isPending ? 'Activating…' : 'Activate'}
            </button>
          </form>

          {/* Divider + footnote */}
          <div className="pt-2 border-t border-gray-200 text-center">
            <p className="text-xs text-gray-400">
              Solutions are included with every MNB chapter test paperback.
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}
